class MibBundleBuilder {
  constructor(root) {
    this.root = root;
    this.currency = root.dataset.currency || 'USD';
    this.cards = [...root.querySelectorAll('[data-bundle-item]')];
    this.submits = [...root.querySelectorAll('[data-bundle-submit]')];
    this.status = root.querySelector('[data-bundle-status]');
    this.tiers = [...root.querySelectorAll('[data-promo-tier]')].map((tier) => ({
      element: tier,
      quantity: Number(tier.dataset.minQuantity),
      discount: Number(tier.dataset.discount)
    })).filter((tier) => Number.isFinite(tier.quantity) && Number.isFinite(tier.discount)).sort((a, b) => a.quantity - b.quantity);
    this.bind();
    this.render();
  }

  bind() {
    this.root.addEventListener('click', (event) => {
      const control = event.target.closest('[data-quantity-change]');
      if (!control) return;
      const card = control.closest('[data-bundle-item]');
      const output = card.querySelector('[data-quantity]');
      const next = Math.max(0, Number(output.value || output.textContent) + Number(control.dataset.quantityChange));
      output.value = next;
      output.textContent = next;
      this.render();
    });

    this.root.addEventListener('change', (event) => {
      if (event.target.matches('[name^="purchase-option-"]')) this.render();
    });

    this.submits.forEach((button) => button.addEventListener('click', () => this.addToCart(button)));
  }

  selections() {
    const subscription = this.purchaseOption() === 'subscription';
    return this.cards.map((card) => ({
      id: Number(card.dataset.variantId),
      title: card.querySelector('h2').textContent.trim(),
      price: subscription && card.dataset.subscriptionPrice ? Number(card.dataset.subscriptionPrice) : Number(card.dataset.price),
      sellingPlan: subscription ? Number(card.dataset.sellingPlan) : null,
      quantity: Number(card.querySelector('[data-quantity]').value || card.querySelector('[data-quantity]').textContent)
    })).filter((item) => item.quantity > 0);
  }

  purchaseOption() {
    return this.root.querySelector('[name^="purchase-option-"]:checked')?.value || 'one-time';
  }

  money(cents) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(cents / 100);
  }

  tierForCount(count) {
    return this.tiers.filter((tier) => count >= tier.quantity).slice(-1)[0] || null;
  }

  render() {
    const subscription = this.purchaseOption() === 'subscription';
    const selections = this.selections();
    const count = selections.reduce((sum, item) => sum + item.quantity, 0);
    const regularTotal = selections.reduce((sum, item) => sum + (Number(this.cards.find((card) => Number(card.dataset.variantId) === item.id)?.dataset.price) * item.quantity), 0);
    const subtotal = selections.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const activeTier = this.tierForCount(count);
    const tierDiscount = activeTier?.discount || 0;
    const total = Math.round(subtotal * (100 - tierDiscount) / 100);
    const list = this.root.querySelector('[data-summary-items]');

    this.cards.forEach((card) => {
      const quantity = Number(card.querySelector('[data-quantity]').value || card.querySelector('[data-quantity]').textContent);
      card.classList.toggle('is-selected', quantity > 0);
      card.querySelector('[data-quantity-change="-1"]').disabled = quantity === 0;
      const regularPrice = Number(card.dataset.price);
      const purchasePrice = subscription && card.dataset.subscriptionPrice ? Number(card.dataset.subscriptionPrice) : regularPrice;
      const currentPrice = Math.round(purchasePrice * (100 - tierDiscount) / 100);
      const cardCompare = card.querySelector('[data-card-compare-price]');
      cardCompare.hidden = currentPrice >= regularPrice;
      cardCompare.textContent = this.money(regularPrice);
      card.querySelector('[data-card-price]').textContent = this.money(currentPrice);
    });

    list.innerHTML = selections.map((item) => `<li><span>${item.quantity} × ${this.escape(item.title)}</span><strong>${this.money(item.price * item.quantity)}</strong></li>`).join('');
    this.root.querySelector('[data-summary-empty]').hidden = selections.length > 0;
    this.root.querySelector('[data-total-count]').textContent = count;
    this.root.querySelector('[data-item-label]').textContent = count === 1 ? 'item' : 'items';
    this.root.querySelector('[data-total-price]').textContent = this.money(total);
    const compare = this.root.querySelector('[data-compare-total]');
    compare.hidden = total >= regularTotal || regularTotal === 0;
    compare.textContent = this.money(regularTotal);
    const savings = this.root.querySelector('[data-savings-message]');
    const saved = Math.max(0, regularTotal - total);
    savings.hidden = saved === 0;
    savings.textContent = saved > 0 ? `${this.root.dataset.savingsPrefix} ${this.money(saved)}${activeTier ? ` · ${activeTier.discount}% ${this.root.dataset.tierActiveSuffix}` : ''}` : '';
    this.tiers.forEach((tier) => tier.element.classList.toggle('is-active', tier === activeTier));
    this.root.querySelector('[data-purchase-summary]').textContent = subscription ? this.root.dataset.subscriptionSummary : this.root.dataset.oneTimeSummary;
    this.submits.forEach((button) => { button.disabled = count === 0; });

    const sticky = this.root.querySelector('[data-sticky-checkout]');
    if (sticky) {
      sticky.hidden = count === 0;
      sticky.querySelector('[data-sticky-count]').textContent = count;
      sticky.querySelector('[data-sticky-item-label]').textContent = count === 1 ? this.root.dataset.itemSingular : this.root.dataset.itemPlural;
      sticky.querySelector('[data-sticky-total]').textContent = this.money(total);
      sticky.querySelector('[data-sticky-savings]').textContent = saved > 0 ? `${this.root.dataset.savingsPrefix} ${this.money(saved)}` : this.root.dataset.noSavingsText;
      const stickyCompare = sticky.querySelector('[data-sticky-compare]');
      stickyCompare.hidden = total >= regularTotal || regularTotal === 0;
      stickyCompare.textContent = this.money(regularTotal);
    }
  }

  escape(value) {
    const node = document.createElement('span');
    node.textContent = value;
    return node.innerHTML;
  }

  async addToCart(sourceButton) {
    const selections = this.selections();
    if (this.purchaseOption() === 'subscription' && selections.some((item) => !item.sellingPlan)) {
      this.status.textContent = this.root.dataset.planErrorText;
      return;
    }
    const items = selections.map(({ id, quantity, sellingPlan }) => {
      const item = { id, quantity };
      if (sellingPlan) item.selling_plan = sellingPlan;
      return item;
    });
    if (!items.length) return;
    this.submits.forEach((button) => { button.disabled = true; });
    sourceButton.setAttribute('aria-busy', 'true');
    this.status.textContent = this.root.dataset.loadingText;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) throw new Error('Cart request failed');
      window.location.assign(`${window.Shopify?.routes?.root || '/'}${sourceButton.dataset.checkout === 'true' ? 'checkout' : 'cart'}`);
    } catch (error) {
      this.status.textContent = this.root.dataset.errorText;
      this.submits.forEach((button) => { button.disabled = false; });
      sourceButton.removeAttribute('aria-busy');
    }
  }
}

const initMibBundles = (scope = document) => scope.querySelectorAll('[data-mib-bundle]').forEach((root) => {
  if (!root.mibBundleBuilder) root.mibBundleBuilder = new MibBundleBuilder(root);
});

document.addEventListener('DOMContentLoaded', () => initMibBundles());
document.addEventListener('shopify:section:load', (event) => initMibBundles(event.target));
